<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import StudioSection from './StudioSection.vue';
import { barWidthClass } from './barWidth';

// Who answered the members, per day (stats.bot_vs_cs). Every conversation the
// bot saw that day sits in exactly one bucket, so the three totals add up.
const props = defineProps({
  report: {
    type: Object,
    default: () => ({}),
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const BUCKETS = [
  { key: 'bot_alone', label: 'BOT_ALONE', color: 'bg-n-teal-9' },
  { key: 'handed_to_cs', label: 'HANDED_TO_CS', color: 'bg-n-amber-9' },
  { key: 'no_answer', label: 'NO_ANSWER', color: 'bg-n-ruby-9' },
];

const totals = computed(() => props.report?.totals || {});
const total = computed(() => totals.value.conversations || 0);

const percent = value =>
  `${total.value ? Math.round((value / total.value) * 100) : 0}%`;

const tiles = computed(() =>
  BUCKETS.map(bucket => ({
    ...bucket,
    value: totals.value[bucket.key] || 0,
    share: t('AI_AGENT.REPORT.SHARE', {
      share: percent(totals.value[bucket.key] || 0),
      total: total.value.toLocaleString(),
    }),
  }))
);

const formatDay = date =>
  new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

// Busiest day sets the full bar width; quiet days are left out of the list.
const days = computed(() => {
  const active = (props.report?.days || []).filter(day => day.conversations);
  const max = Math.max(...active.map(day => day.conversations), 0);
  return active
    .slice()
    .reverse()
    .map(day => ({
      ...day,
      label: formatDay(day.date),
      segments: BUCKETS.map(bucket => ({
        key: bucket.key,
        color: bucket.color,
        widthClass: barWidthClass(day[bucket.key], max),
      })),
    }));
});
</script>

<template>
  <StudioSection
    :title="$t('AI_AGENT.REPORT.TITLE')"
    :description="$t('AI_AGENT.REPORT.HELP')"
    icon="i-lucide-users-round"
  >
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div
        v-for="tile in tiles"
        :key="tile.key"
        class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1"
        :data-testid="`bot-vs-cs-${tile.key}`"
      >
        <span class="flex items-center gap-2 text-label-small text-n-slate-11">
          <span class="rounded-full size-2 shrink-0" :class="tile.color" />
          {{ $t(`AI_AGENT.REPORT.${tile.label}`) }}
        </span>
        <span
          class="text-2xl font-semibold tracking-tight tabular-nums text-n-slate-12"
        >
          {{ isFetching ? '—' : tile.value.toLocaleString() }}
        </span>
        <span class="tabular-nums text-label-small text-n-slate-11">
          {{ tile.share }}
        </span>
        <span class="text-label-small text-n-slate-10">
          {{ $t(`AI_AGENT.REPORT.DEFINITION.${tile.label}`) }}
        </span>
      </div>
    </div>

    <p
      v-if="!days.length && !isFetching"
      class="py-6 text-center text-body-main text-n-slate-10"
    >
      {{ $t('AI_AGENT.REPORT.EMPTY') }}
    </p>
    <ul v-else class="flex flex-col gap-2">
      <li
        v-for="day in days"
        :key="day.date"
        class="grid items-center grid-cols-[6.5rem_1fr_auto] gap-3"
        :data-testid="`bot-vs-cs-day-${day.date}`"
      >
        <span class="truncate text-label-small text-n-slate-11">
          {{ day.label }}
        </span>
        <div class="flex h-2 overflow-hidden rounded-full bg-n-alpha-2">
          <div
            v-for="segment in day.segments"
            :key="segment.key"
            class="h-2"
            :class="[segment.color, segment.widthClass]"
          />
        </div>
        <span
          class="flex items-center justify-end gap-2 tabular-nums text-label-small"
        >
          <span
            v-if="day.no_answer"
            v-tooltip="$t('AI_AGENT.REPORT.NO_ANSWER')"
            class="px-1.5 rounded-md bg-n-ruby-5 text-n-ruby-11"
            :data-testid="`bot-vs-cs-missed-${day.date}`"
          >
            {{ day.no_answer }}
          </span>
          <span class="text-n-slate-12">
            {{ $t('AI_AGENT.REPORT.DAY_TOTAL', { n: day.conversations }) }}
          </span>
        </span>
      </li>
    </ul>
  </StudioSection>
</template>
